import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { getTraumaLessons } from "@/lib/trauma/attempts.functions";
import { TRAUMA_RESULT_UI as UI, TRAUMA_PLAYER_UI } from "@/lib/trauma/ui-copy";
import { TraumaLesson } from "@/components/trauma/lesson";
import { AxisHeader, TraumaTopBar } from "@/components/trauma/chrome";
import { MarkLast, readingEyebrow, readingH1 } from "@/components/public/reading-page";

const lessonsQuery = (courseId: string) => queryOptions({
  queryKey: ["trauma", "lessons", courseId],
  queryFn: () => getTraumaLessons({ data: { courseId } }),
  gcTime: 0,
  retry: false,
});

export const Route = createFileRoute("/_authenticated/courses/trauma/$id/lessons")({
  // `r` = the result attempt the reader came from, only used for the «رجوع لنتيجتك» link.
  validateSearch: (s: Record<string, unknown>): { r?: string } =>
    typeof s.r === "string" && UUID.test(s.r) ? { r: s.r } : {},
  loader: async ({ context, params }) => {
    try { return await context.queryClient.ensureQueryData(lessonsQuery(params.id)); }
    catch { throw redirect({ to: "/courses/trauma/$id/play", params: { id: params.id } }); }
  },
  gcTime: 0,
  head: () => ({ meta: [
    { title: `${UI.lessonsTitle} — د. سارة النحّاس` },
    { name: "description", content: "قراءة دروس صدمات الطفولة من د. سارة النحّاس." },
    { property: "og:title", content: `${UI.lessonsTitle} — د. سارة النحّاس` },
    { property: "og:description", content: "قراءة دروس صدمات الطفولة." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  errorComponent: () => <Link to="/portal/courses">{UI.myCourses}</Link>,
  notFoundComponent: () => <Link to="/portal/courses">{UI.myCourses}</Link>,
  component: LessonsPage,
});

const primaryPill =
  `${btn.primary}`;
const ghostPill =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill px-5 font-display text-[16px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50";

function LessonsPage() {
  const initialData = Route.useLoaderData();
  const { id } = Route.useParams();
  const { r } = Route.useSearch();
  const { data } = useSuspenseQuery({ ...lessonsQuery(id), initialData });
  const two = (n: number) => String(n).padStart(2, "0");
  return (
    <main dir="rtl" className="min-h-dvh bg-cream-50 font-body text-[17px] leading-[1.85] text-brown-700 [&_h1]:tracking-normal [&_h2]:tracking-normal [&_h3]:tracking-normal">
      <TraumaTopBar mode="back" title={data.title} />
      <div className="mx-auto max-w-[44rem] px-5 py-10 lg:py-14">
        <p className={readingEyebrow}>{data.title}</p>
        <h1 className={readingH1}><MarkLast text={UI.lessonsTitle} /></h1>
        <nav aria-label={TRAUMA_PLAYER_UI.axesIndex} className="mt-8">
          <ol className="border-y border-hairline-subtle">
            {data.lessons.map((axis) => (
              <li key={axis.n} className="border-b border-hairline-subtle last:border-b-0">
                <a href={`#axis-${axis.n}`} className="flex min-h-12 items-center gap-4 py-2 font-display text-[17px] font-bold transition-colors duration-ds-fast hover:text-amber-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50">
                  <span className="w-8 shrink-0 text-[19px] font-extrabold text-amber-800" dir="ltr">{two(axis.n)}</span>
                  <span className="min-w-0 flex-1">{axis.label}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <div className="mt-12">
          {data.lessons.map((axis, i) => {
            const next = data.lessons[i + 1];
            return (
              <section key={axis.n} id={`axis-${axis.n}`} aria-labelledby={`axis-${axis.n}-heading`} className="scroll-mt-20 border-t border-hairline-subtle pt-10 pb-10 first:border-t-0 first:pt-0">
                <AxisHeader as="h2" headingId={`axis-${axis.n}-heading`} n={axis.n} label={axis.label} question={axis.question} />
                <div className="mt-6"><TraumaLesson lesson={axis.lesson} /></div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  {next ? <a href={`#axis-${next.n}`} className={primaryPill}>{TRAUMA_PLAYER_UI.nextLesson} ←</a> : null}
                  {r ? (
                    <Link to="/courses/trauma/$id/result/$attemptId" params={{ id, attemptId: r }} className={ghostPill}>{TRAUMA_PLAYER_UI.backToResult} →</Link>
                  ) : (
                    <Link to="/portal/courses" className={ghostPill}>{UI.myCourses} →</Link>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </main>
  );
}
